import React, { useEffect, useRef, useState } from 'react';
import { Plus, Search, Pencil, Trash2, ChevronLeft, ChevronRight, Upload, Download } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent } from '../ui/card';
import { Alert, AlertDescription } from '../ui/alert';
import { Badge } from '../ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../ui/dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../ui/table';
import {
  holidaysAdminService, type HolidayRow, type BulkUploadResponse,
} from '../../services/holidaysAdminService';

const PAGE_SIZE = 25;
const STATES = ['', 'NAT', 'QLD', 'VIC', 'NSW', 'SA', 'ACT', 'TAS'];
const CURRENT_YEAR = new Date().getFullYear();
const YEAR_OPTIONS = ['', String(CURRENT_YEAR - 1), String(CURRENT_YEAR), String(CURRENT_YEAR + 1)];

type HolidayForm = { name: string; date: string; state: string };

const EMPTY_FORM: HolidayForm = { name: '', date: '', state: 'NAT' };

const formatDate = (d: string) => {
  if (!d) return '—';
  const [y, m, day] = d.split('-');
  return `${day}/${m}/${y}`;
};

export const HolidaysManagement: React.FC = () => {
  const [rows, setRows] = useState<HolidayRow[]>([]);
  const [count, setCount] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [stateFilter, setStateFilter] = useState('');
  const [yearFilter, setYearFilter] = useState('');

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<HolidayRow | null>(null);
  const [form, setForm] = useState<HolidayForm>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  const [bulkFile, setBulkFile] = useState<File | null>(null);
  const [bulkUploading, setBulkUploading] = useState(false);
  const [bulkResult, setBulkResult] = useState<BulkUploadResponse | null>(null);
  const bulkFileRef = useRef<HTMLInputElement>(null);

  const totalPages = Math.max(1, Math.ceil(count / PAGE_SIZE));

  useEffect(() => {
    const t = setTimeout(() => { setSearch(searchInput.trim()); setPage(1); }, 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  useEffect(() => { setPage(1); }, [stateFilter, yearFilter]);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(null);
    holidaysAdminService.list({ search, state: stateFilter, year: yearFilter, page, limit: PAGE_SIZE })
      .then((res) => {
        if (!active) return;
        if (!res.success) { setError(res.error ?? 'Failed to load'); setRows([]); setCount(0); }
        else { setRows(res.data ?? []); setCount(res.count ?? 0); }
        setLoading(false);
      });
    return () => { active = false; };
  }, [search, stateFilter, yearFilter, page]);

  const refresh = () => {
    setLoading(true);
    holidaysAdminService.list({ search, state: stateFilter, year: yearFilter, page, limit: PAGE_SIZE })
      .then((res) => {
        if (res.success) { setRows(res.data ?? []); setCount(res.count ?? 0); }
        setLoading(false);
      });
  };

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setFormError(null);
    setDialogOpen(true);
  };

  const openEdit = (row: HolidayRow) => {
    setEditing(row);
    setForm({ name: row.name, date: row.date, state: row.state });
    setFormError(null);
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) { setFormError('Name is required'); return; }
    if (!form.date) { setFormError('Date is required'); return; }
    if (!form.state) { setFormError('State is required'); return; }

    setSaving(true);
    setFormError(null);
    const body = { name: form.name.trim(), date: form.date, state: form.state };
    const res = editing
      ? await holidaysAdminService.update(editing.id, body)
      : await holidaysAdminService.create(body);
    setSaving(false);

    if (!res.success) { setFormError(res.error ?? 'Save failed'); return; }
    setDialogOpen(false);
    refresh();
  };

  const downloadSample = () => {
    const csv = [
      'name,date,state',
      'Christmas Day,2025-12-25,NAT',
      'Boxing Day,2025-12-26,NAT',
      "New Year's Day,2026-01-01,NAT",
      'Australia Day,2026-01-26,NAT',
      'Good Friday,2026-04-03,NAT',
      'Easter Saturday,2026-04-04,NAT',
      'Easter Monday,2026-04-06,NAT',
      'ANZAC Day,2026-04-25,NAT',
      "Queen's Birthday,2026-06-08,QLD",
      'Labour Day,2026-05-04,QLD',
      'Melbourne Cup Day,2026-11-03,VIC',
      'Adelaide Cup,2026-05-18,SA',
    ].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'public_holidays_sample.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const openBulkDialog = () => {
    setBulkFile(null);
    setBulkResult(null);
    setBulkDialogOpen(true);
  };

  const handleBulkFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0] ?? null;
    setBulkFile(f);
    setBulkResult(null);
  };

  const handleBulkUpload = async () => {
    if (!bulkFile) return;
    setBulkUploading(true);
    setBulkResult(null);
    const result = await holidaysAdminService.bulkUpload(bulkFile);
    setBulkResult(result);
    setBulkUploading(false);
    if (result.success && (result.inserted ?? 0) > 0) refresh();
  };

  const handleDelete = async (row: HolidayRow) => {
    if (!window.confirm(`Delete "${row.name}" (${row.state} – ${formatDate(row.date)})? This cannot be undone.`)) return;
    const res = await holidaysAdminService.remove(row.id);
    if (!res.success) { setError(res.error ?? 'Delete failed'); return; }
    if (rows.length === 1 && page > 1) setPage((p) => p - 1);
    else refresh();
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">Public Holidays</h1>
        <p className="text-sm text-muted-foreground">
          Australian public holiday reference data used to classify TOU rates on holidays as off-peak.
        </p>
      </div>

      {/* Filters + toolbar */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative min-w-[200px] flex-1 max-w-xs">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search holiday name…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="pl-8"
          />
        </div>

        <select
          value={stateFilter}
          onChange={(e) => setStateFilter(e.target.value)}
          className="h-10 rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          {STATES.map((s) => <option key={s} value={s}>{s || 'All states'}</option>)}
        </select>

        <select
          value={yearFilter}
          onChange={(e) => setYearFilter(e.target.value)}
          className="h-10 rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          {YEAR_OPTIONS.map((y) => <option key={y} value={y}>{y || 'All years'}</option>)}
        </select>

        <span className="text-sm text-muted-foreground">{count.toLocaleString()} rows</span>
        <div className="flex-1" />
        <Button variant="outline" onClick={openBulkDialog} className="gap-1.5">
          <Upload className="h-4 w-4" /> Bulk upload
        </Button>
        <Button onClick={openCreate} className="gap-1.5">
          <Plus className="h-4 w-4" /> Add holiday
        </Button>
      </div>

      {error && <Alert variant="destructive"><AlertDescription>{error}</AlertDescription></Alert>}

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Date</TableHead>
                <TableHead>State</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={4} className="text-center text-muted-foreground py-8">Loading…</TableCell></TableRow>
              ) : rows.length === 0 ? (
                <TableRow><TableCell colSpan={4} className="text-center text-muted-foreground py-8">No holidays found</TableCell></TableRow>
              ) : rows.map((row) => (
                <TableRow key={row.id}>
                  <TableCell className="font-medium">{row.name}</TableCell>
                  <TableCell className="font-mono text-sm">{formatDate(row.date)}</TableCell>
                  <TableCell>
                    <Badge variant={row.state === 'NAT' ? 'default' : 'outline'}>{row.state}</Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Button variant="ghost" size="sm" onClick={() => openEdit(row)} aria-label="Edit"><Pencil className="h-4 w-4" /></Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDelete(row)} aria-label="Delete"><Trash2 className="h-4 w-4 text-red-600" /></Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <div className="flex items-center justify-between">
        <span className="text-sm text-muted-foreground">Page {page} of {totalPages}</span>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" disabled={page <= 1 || loading} onClick={() => setPage((p) => Math.max(1, p - 1))}>
            <ChevronLeft className="h-4 w-4" /> Prev
          </Button>
          <Button variant="outline" size="sm" disabled={page >= totalPages || loading} onClick={() => setPage((p) => Math.min(totalPages, p + 1))}>
            Next <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Bulk upload dialog */}
      <Dialog open={bulkDialogOpen} onOpenChange={(open) => { if (!bulkUploading) setBulkDialogOpen(open); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Bulk Upload Holidays</DialogTitle>
            <DialogDescription>
              Upload a CSV or XLSX file. Required columns: <strong>name</strong>, <strong>date</strong> (YYYY-MM-DD), <strong>state</strong> (NAT, QLD, VIC, NSW, SA, ACT, TAS).
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <button
              type="button"
              onClick={downloadSample}
              className="flex items-center gap-1.5 text-sm text-primary hover:underline"
            >
              <Download className="h-4 w-4" /> Download sample CSV
            </button>

            <div
              className="flex flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed border-input p-6 cursor-pointer hover:bg-muted/40 transition-colors"
              onClick={() => bulkFileRef.current?.click()}
            >
              <Upload className="h-8 w-8 text-muted-foreground" />
              {bulkFile ? (
                <span className="text-sm font-medium">{bulkFile.name}</span>
              ) : (
                <span className="text-sm text-muted-foreground">Click to select CSV or XLSX file</span>
              )}
              <input
                ref={bulkFileRef}
                type="file"
                accept=".csv,.xlsx"
                className="hidden"
                onChange={handleBulkFileChange}
              />
            </div>

            {bulkResult && (
              <div className="space-y-2">
                {bulkResult.success ? (
                  <Alert>
                    <AlertDescription className="space-y-1">
                      <div className="font-medium">Upload complete</div>
                      <div className="text-sm">
                        {bulkResult.inserted} inserted · {bulkResult.skipped} skipped · {(bulkResult.errors?.length ?? 0)} errors
                        {' '}(of {bulkResult.total} rows)
                      </div>
                    </AlertDescription>
                  </Alert>
                ) : (
                  <Alert variant="destructive">
                    <AlertDescription>{bulkResult.error}</AlertDescription>
                  </Alert>
                )}
                {(bulkResult.errors?.length ?? 0) > 0 && (
                  <div className="max-h-36 overflow-y-auto rounded-md border bg-muted/30 p-2 text-xs space-y-1">
                    {bulkResult.errors!.map((e) => (
                      <div key={e.row} className="text-red-600">Row {e.row}: {e.error}</div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkDialogOpen(false)} disabled={bulkUploading}>
              {bulkResult?.success ? 'Close' : 'Cancel'}
            </Button>
            {!bulkResult?.success && (
              <Button onClick={handleBulkUpload} disabled={!bulkFile || bulkUploading}>
                {bulkUploading ? 'Uploading…' : 'Upload'}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit holiday' : 'Add holiday'}</DialogTitle>
            <DialogDescription>
              {editing ? 'Update the holiday details.' : 'Add a new public holiday record.'}
            </DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-1 gap-4 py-2">
            <div className="space-y-1.5">
              <Label htmlFor="fld-name">Name <span className="text-red-500">*</span></Label>
              <Input
                id="fld-name"
                placeholder="e.g. Christmas Day"
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="fld-date">Date <span className="text-red-500">*</span></Label>
              <Input
                id="fld-date"
                type="date"
                value={form.date}
                onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))}
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="fld-state">State <span className="text-red-500">*</span></Label>
              <select
                id="fld-state"
                value={form.state}
                onChange={(e) => setForm((f) => ({ ...f, state: e.target.value }))}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                {STATES.filter(Boolean).map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          </div>

          {formError && <Alert variant="destructive"><AlertDescription>{formError}</AlertDescription></Alert>}

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} disabled={saving}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving}>{saving ? 'Saving…' : 'Save'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default HolidaysManagement;
